"use client";

import { useEffect, useMemo, useRef, type MutableRefObject } from "react";
import log from "logbeacon";
import { useSnapshot } from "valtio";
import { LowCreditModalCreateAble } from "../components";
import { EdgenModalCreateAble } from "../components/EdgenModal";
import { ModalStore } from "@store/modules/modal";

import { LOCAL_STORAGE_KEYS } from "../config";
import { OpportunityTypeEnum } from "../features/api/memberApi";
import { rootStore } from "../store";
import { isInterruptiveSurfaceRoute } from "@/src/hooks/onboarding/isOnboardingRoute";
import { usePathname } from "@/src/hooks/usePathname";
import { normalizePathname } from "@/src/utils/routeNormalizer";
import { useCredits } from "./useCredits";
import { useGlobalAppModalLimit } from "./useGlobalAppModalLimit";
import { useMemberUser } from "./useMemberUser";
import { useOpportunitySummary } from "./useOpportunities";

const GLOBAL_APP_MODAL_KEY_LOW_CREDIT = "low-credit";

/** 配置参数 - 可根据需求调整 */
const LOW_CREDIT_MODAL_CONFIG = {
  /** Credits 阈值，低于此值触发弹窗 */
  CREDIT_THRESHOLD: 50,
  /** 展示间隔（毫秒），默认 1 天 */
  SHOW_INTERVAL_MS: 24 * 60 * 60 * 1000,
  /** 最大关闭次数，超过后永久不再提示 */
  MAX_DISMISS_COUNT: 3,
} as const;

/**
 * 展示低余额弹窗并记录关闭次数
 */
async function showLowCreditModal(
  credits: number,
  currentDismissCount: number,
  isOpenRef: MutableRefObject<boolean>,
) {
  if (
    ModalStore.appVersionUpdateModalVisible ||
    ModalStore.turnOnNotificationModalVisible ||
    ModalStore.appRatingModalVisible ||
    ModalStore.trackerWelcomeModalVisible
  ) {
    return;
  }

  isOpenRef.current = true;
  ModalStore.lowCreditModalVisible = true;

  localStorage.setItem(
    LOCAL_STORAGE_KEYS.LOW_CREDIT_MODAL_LAST_SHOWN,
    Date.now().toString(),
  );
  try {
    await LowCreditModalCreateAble({ credits });
    localStorage.setItem(
      LOCAL_STORAGE_KEYS.LOW_CREDIT_MODAL_DISMISS_COUNT,
      (currentDismissCount + 1).toString(),
    );
  } catch (error) {
    log.warn("[useLowCreditModal]: Failed to show Low Credit modal.", error);
  } finally {
    isOpenRef.current = false;
    ModalStore.lowCreditModalVisible = false;
  }
}
/**
 * 低余额弹窗 Hook
 *
 * 功能说明：
 * - 免费用户 + Credits < 50 时触发
 * - 每天最多展示 1 次
 * - 连续关闭 3 次后不再提示
 *
 * @example
 * ```tsx
 * // 在 HomePage 中使用
 * import { useLowCreditModal } from "@/src/hooks/useLowCreditModal";
 *
 * export const HomePage = () => {
 *   useLowCreditModal();
 *   return <div>...</div>;
 * };
 * ```
 */
export const useAlphaModal = () => {
  const hasCreditShownRef = useRef(false);
  const isLowCreditOpenRef = useRef(false);
  const pathname = usePathname();
  const { lowCreditModalVisible } = useSnapshot(ModalStore);
  const { scheduleOpen, tryOpen, canShow, isSuppressed } =
    useGlobalAppModalLimit(GLOBAL_APP_MODAL_KEY_LOW_CREDIT, {
      hideInAppChatModal: true,
      onRemoteClaim: () => {
        isLowCreditOpenRef.current = false;
        ModalStore.lowCreditModalVisible = false;
        EdgenModalCreateAble.end(false);
      },
    });

  useEffect(() => {
    if (!lowCreditModalVisible && isLowCreditOpenRef.current) {
      isLowCreditOpenRef.current = false;
      EdgenModalCreateAble.end(false);
    }
  }, [lowCreditModalVisible]);
  const scheduleOpenRef = useRef(scheduleOpen);
  const tryOpenRef = useRef(tryOpen);
  scheduleOpenRef.current = scheduleOpen;
  tryOpenRef.current = tryOpen;
  const { credits } = useCredits();

  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { data: summaryData } = useOpportunitySummary({
    params: {
      types: [
        OpportunityTypeEnum.AI_PICK,
        OpportunityTypeEnum.EARNINGS,
        OpportunityTypeEnum.IPO,
      ],
    },
    enabled: isLoggedIn,
  });

  // 使用会员信息判断是否为免费用户
  const { data: memberInfo, isLoading: isMemberLoading } = useMemberUser();

  // 计算是否应展示 Alpha 解锁弹窗
  const shouldShowAlphaModal = useMemo(() => {
    const isShowGiftModal = summaryData?.every((item) => !item.gifted_count);
    return isShowGiftModal;
  }, [summaryData]);

  // 1. 优先展示 Alpha 解锁弹窗（如果满足条件）
  // 2. 只有在 Alpha 解锁弹窗不展示的情况下，才考虑展示低余额弹窗
  // 2026/4/7 主题色修改 和 佳琪沟通，去掉这个弹窗显示
  // useEffect(() => {
  //   if (isSummaryLoading || !isLoggedIn || !memberInfo?.isFree) {
  //     return;
  //   }
  //   if (shouldShowAlphaModal && !hasAlphaShownRef.current) {
  //     hasAlphaShownRef.current = true;
  //     // 延迟 2 秒，等待 EdgenModalCreateAble.Root (lazyHydrate + requestIdleCallback) 挂载完成
  //     const timer = setTimeout(() => {
  //       showAlphaUnlockedModal(deviceDetector.isMobile() ?? false);
  //     }, 2000);

  //     return () => clearTimeout(timer);
  //   }
  // }, [isSummaryLoading, isLoggedIn, memberInfo?.isFree, shouldShowAlphaModal]);

  useEffect(() => {
    // 防止重复执行
    if (hasCreditShownRef.current) {
      return;
    }
    if (!isLoggedIn || isMemberLoading) {
      return;
    }

    if (shouldShowAlphaModal) {
      return;
    }
    // 等待 memberInfo 数据准备好（防止登录后数据还没加载就判断）
    if (memberInfo === undefined || memberInfo === null) {
      return;
    }

    // 非免费用户不触发（Pro/Expert用户跳过）
    if (!memberInfo.isFree) {
      return;
    }
    // Credits 余额检查
    if (
      credits === undefined ||
      credits >= LOW_CREDIT_MODAL_CONFIG.CREDIT_THRESHOLD
    ) {
      return;
    }

    // 检查关闭次数
    const dismissCount = parseInt(
      localStorage.getItem(LOCAL_STORAGE_KEYS.LOW_CREDIT_MODAL_DISMISS_COUNT) ||
        "0",
      10,
    );
    if (dismissCount >= LOW_CREDIT_MODAL_CONFIG.MAX_DISMISS_COUNT) {
      return;
    }

    // 检查是否在冷却期内（24小时）
    const lastShown = localStorage.getItem(
      LOCAL_STORAGE_KEYS.LOW_CREDIT_MODAL_LAST_SHOWN,
    );
    if (lastShown) {
      const lastShownTime = parseInt(lastShown, 10);
      if (
        Date.now() - lastShownTime <
        LOW_CREDIT_MODAL_CONFIG.SHOW_INTERVAL_MS
      ) {
        return; // 24小时内已展示过
      }
    }

    if (isSuppressed) {
      hasCreditShownRef.current = true;
      return;
    }
    if (!canShow) {
      return;
    }

    if (isInterruptiveSurfaceRoute(pathname)) {
      return;
    }

    hasCreditShownRef.current = true;

    return scheduleOpenRef.current(2500, () => {
      if (
        typeof window !== "undefined" &&
        isInterruptiveSurfaceRoute(normalizePathname(window.location.pathname))
      ) {
        return;
      }
      tryOpenRef.current(() => {
        void showLowCreditModal(credits, dismissCount, isLowCreditOpenRef);
      });
    });
  }, [
    credits,
    isLoggedIn,
    memberInfo,
    isMemberLoading,
    shouldShowAlphaModal,
    canShow,
    isSuppressed,
    pathname,
  ]);
};
